// Change #100462 - Enterprise billing in the add-user wizard.
//
// An Enterprise company never buys licences: CRMit set up its plan with the
// seats agreed (MaxUsers) and a tolerance above that. So instead of a plan
// picker, prices and a card, the wizard shows how many seats are in use and
// whether one more person fits. The backend enforces the same rule.
//
// The tolerance is secret (Paul): the customer never sees it. The backend
// leaves it out and sends CanAdd; over the users included the panel goes red.

import React from "react";
import { useSelector } from "react-redux";

/** subscriptionbilling from /subscriptionproduct/list/company/available. */
export const useSubscriptionBilling = () =>
  useSelector(
    (state) => state.ManageLicense.subscriptionProductList?.subscriptionbilling
  );

export const isEnterpriseBilling = (billing) => billing?.Mode === "ENTERPRISE";

/** Can one more person be added? CanAdd from the server; older backends
 * still sent Limit, so fall back to it without ever showing it. */
export const enterpriseCanAdd = (billing) => {
  if (!isEnterpriseBilling(billing) || !billing?.PlanReady) return false;
  if (typeof billing?.CanAdd === "boolean") return billing.CanAdd;
  return billing?.Limit == null || Number(billing.Used) < Number(billing.Limit);
};

export default function EnterpriseSeats({ billing, adding = 1 }) {
  if (!isEnterpriseBilling(billing)) return null;

  if (!billing?.PlanReady) {
    return (
      <div className="alert alert-warning mb-0">
        Your plan has not been set up yet. Please contact CRMit.
      </div>
    );
  }

  const used = Number(billing.Used) || 0;
  const max = billing.MaxUsers != null ? Number(billing.MaxUsers) : null;
  const after = used + adding;
  // adding = 0 is the licence page: report where they are, not what one more
  // person would do.
  const blocked = adding > 0 ? !enterpriseCanAdd(billing) : billing.AtLimit === true || !enterpriseCanAdd(billing);
  const over = max != null && (adding > 0 ? after > max : used > max);
  const pct = max ? Math.min(100, Math.round((used / max) * 100)) : 0;
  const tone = blocked || over ? "danger" : max != null && used >= max ? "warning" : "success";

  return (
    <div className={`border rounded-3 p-3 border-${tone}-subtle bg-${tone}-subtle`}>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <div className="fw-bold">{billing.PlanName}</div>
          <div className="text-muted small">Your users are included in your plan. Nothing is charged here.</div>
        </div>
        <div className="text-end">
          <div className={`fs-4 fw-bold lh-1${blocked || over ? " text-danger" : ""}`}>
            {used}
            <span className="fs-6 text-muted fw-normal"> of {max ?? "unlimited"} users</span>
          </div>
        </div>
      </div>
      {max != null && (
        <div className="progress mt-2" style={{ height: 6 }}>
          <div className={`progress-bar bg-${tone}`} style={{ width: `${pct}%` }} />
        </div>
      )}
      {blocked ? (
        <div className="mt-2 text-danger small fw-semibold">
          <i className="ri-error-warning-line me-1"></i>
          Your plan is full. Remove someone who no longer needs access, or contact CRMit to increase your plan.
        </div>
      ) : (
        over && (
          <div className="mt-2 text-danger small fw-semibold">
            <i className="ri-error-warning-line me-1"></i>
            {adding > 0
              ? `This takes you to ${after} users, above the ${max} on your plan.`
              : `You have ${used} users, above the ${max} on your plan.`}{" "}
            CRMit will be in touch about your plan.
          </div>
        )
      )}
    </div>
  );
}
